import React from "react";
import Spinner from "@src/components/Spinner";
import { ICuratorConversation } from "@src/types/curator";
import {
  formatMessageTimestamp,
  getClientDisplayName,
  getConversationPlaylistSource,
  getConversationStatus,
  getConversationSubject,
  getCuratorDisplayName,
  getDisplayMessages,
} from "../Messages/conversationUtils";
import { useAdminConversationHistory } from "@src/queries/adminCuratorConversationQueries";
import {
  CenteredNote,
  CuratorNamePill,
  ListenerNamePill,
  LoadOlderRow,
  MessageRow,
  MessageText,
  MessageTime,
  MessagesArea,
  PagerButton,
  ReadOnlyFooter,
  StatusPill,
  TranscriptColumn,
  TranscriptContainer,
  TranscriptHeader,
  TranscriptMeta,
  TranscriptTitle,
} from "./AdminMessages.styles";

interface AdminTranscriptProps {
  conversationId: string | null;
  conversation?: ICuratorConversation;
}

const AdminTranscript: React.FC<AdminTranscriptProps> = ({
  conversationId,
  conversation,
}) => {
  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useAdminConversationHistory(conversationId);

  if (!conversationId) {
    return (
      <TranscriptContainer>
        <CenteredNote>Pick a conversation to read its transcript.</CenteredNote>
      </TranscriptContainer>
    );
  }

  // Pages arrive newest-first (each one older than the last), and every page is
  // already oldest→newest internally, so the pages are reversed and the messages
  // inside them are not.
  const rawMessages = [...(data?.pages ?? [])]
    .reverse()
    .flatMap((page) => page.messages);
  // No curator identity is passed: an admin is neither participant, so messages are
  // laid out by role (curator on the right, listener on the left) rather than by "self".
  const messages = getDisplayMessages(rawMessages, conversation);
  const status = getConversationStatus(conversation ?? {});
  const subject = conversation ? getConversationSubject(conversation) : null;
  const playlistSource = conversation
    ? getConversationPlaylistSource(conversation)
    : null;

  return (
    <TranscriptContainer>
      <TranscriptColumn>
        <TranscriptHeader>
          <TranscriptTitle>
            {conversation
              ? `${getCuratorDisplayName(conversation)} ↔ ${getClientDisplayName(conversation)}`
              : "Conversation"}
          </TranscriptTitle>
          <TranscriptMeta>
            <StatusPill $status={status}>{status.replace("_", " ")}</StatusPill>
            {subject ? ` Subject: ${subject}` : ""}
            {playlistSource ? ` · From: ${playlistSource}` : ""}
          </TranscriptMeta>
        </TranscriptHeader>

        <MessagesArea>
          {isPending && (
            <CenteredNote>
              <Spinner />
            </CenteredNote>
          )}
          {!isPending && isError && (
            <CenteredNote>Could not load this transcript.</CenteredNote>
          )}
          {!isPending && !isError && messages.length === 0 && (
            <CenteredNote>
              This conversation has no messages stored.
            </CenteredNote>
          )}
          {!isPending && !isError && hasNextPage && (
            <LoadOlderRow>
              <PagerButton
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading…" : "Load older messages"}
              </PagerButton>
            </LoadOlderRow>
          )}
          {messages.map((message) => (
            <MessageRow key={message.id} $isCurator={message.isCurator}>
              {message.isCurator ? (
                <CuratorNamePill>{message.senderName}</CuratorNamePill>
              ) : (
                <ListenerNamePill>{message.senderName}</ListenerNamePill>
              )}
              <MessageText $isCurator={message.isCurator}>
                {message.content}
              </MessageText>
              <MessageTime $isCurator={message.isCurator}>
                {formatMessageTimestamp(message.createdAt)}
              </MessageTime>
            </MessageRow>
          ))}
        </MessagesArea>

        <ReadOnlyFooter>
          Read-only. Reading a conversation here does not mark it as read and is
          not visible to the curator or the listener.
        </ReadOnlyFooter>
      </TranscriptColumn>
    </TranscriptContainer>
  );
};

export default AdminTranscript;
